import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { AlertCircle } from "lucide-react";
import { cn } from "../lib/utils";

export default function RooftopStockPressure() {
  const { rooftop_id } = useParams();
  const [rooftop, setRooftop] = useState(null);
  const [aged, setAged] = useState([]);

  useEffect(() => {
    api.get(`/rooftops/${rooftop_id}`).then((r) => setRooftop(r.data));
    api.get(`/acquisitions?rooftop_id=${rooftop_id}&limit=200`).then((r) => {
      const now = Date.now();
      const rows = r.data.filter((a) => !a.retail_date)
        .map((a) => ({ ...a, days: Math.round((now - new Date(a.acquisition_date).getTime()) / 86400000) }))
        .sort((a, b) => b.days - a.days);
      setAged(rows);
    });
  }, [rooftop_id]);

  return (
    <Layout variant="rooftop" rooftopId={rooftop_id} rooftopName={rooftop?.name}>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          <AlertCircle size={12} /> Stock pressure
        </div>
        <h1 className="text-3xl font-bold text-navy mt-1">Aging inventory</h1>
        <p className="text-sm text-slate-500 mt-1 font-medium">Ranked by days on ground · every aged unit is floorplan bleed.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="stock-pressure-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[10px] uppercase tracking-wider text-slate-600">
              <th className="text-left px-4 py-3 font-bold">Days</th>
              <th className="text-left px-4 py-3 font-bold">Vehicle</th>
              <th className="text-right px-4 py-3 font-bold">Acq $</th>
              <th className="text-right px-4 py-3 font-bold">Recon</th>
              <th className="text-left px-4 py-3 font-bold">Action</th>
            </tr>
          </thead>
          <tbody>
            {aged.map((a) => {
              const action = a.days >= 75 ? "wholesale" : a.days >= 60 ? "reprice" : a.days >= 45 ? "re-merchandise" : "monitor";
              const actionColor = a.days >= 75 ? "bg-freddy-red text-white" : a.days >= 60 ? "bg-verdict-conditional/20 text-verdict-conditionalText" : "bg-slate-100 text-slate-700";
              return (
                <tr key={a.acquisition_id} className={cn("border-b border-slate-100", a.days >= 60 && "bg-freddy-red/5")}>
                  <td className="px-4 py-3 tabular font-bold">{a.days}d</td>
                  <td className="px-4 py-3 font-semibold text-navy">{a.year} {a.make} {a.model} <span className="text-slate-500 font-normal">{a.trim}</span></td>
                  <td className="px-4 py-3 text-right tabular">${a.acquisition_price.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular text-slate-500">${a.recon_actual.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={cn("inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider", actionColor)}>{action}</span>
                  </td>
                </tr>
              );
            })}
            {aged.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">No aged units. Lot is tight.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
